import type { RuntimeHomeSettings } from '../../hooks/runtime/cmsApi'
import { useShopI18n } from '../../../i18n'
import { useCart } from '../../../cart'
import { useCustomerAuth } from '../../../customer-auth'
import ShopSearchBar from '../../../components/ShopSearchBar'
import CartIcon from '../../../components/icons/CartIcon'
import BackofficeIcon from '../../../components/icons/BackofficeIcon'
import { HeaderCustomerMenu } from './HeaderCustomerMenu'
import { resolveCustomerMenuLabels, resolveHeaderLinks, resolveLinkLabel } from './headerLinks'
import { resolveProfileIdentity } from './profileIdentity'

type Props = {
  homeSettings: RuntimeHomeSettings
  isAdmin: boolean
  itemCount: number
}

export const HomeFallbackHeader = ({ homeSettings, isAdmin, itemCount }: Props) => {
  const { locale, language, t, toLocalizedPath } = useShopI18n()
  const { openDrawer, clearError } = useCart()
  const { authenticated, busy: authBusy, profile, logout } = useCustomerAuth()
  const backofficeHref = isAdmin ? '/admin' : '/dashboard/login'
  const homeHref = toLocalizedPath('/')
  const { profileName, profileInitials } = resolveProfileIdentity({
    displayName: profile?.displayName,
    email: profile?.email,
  })
  const customerMenuLabels = resolveCustomerMenuLabels(language)
  const links = resolveHeaderLinks({ links: homeSettings.headerLinks, language })

  return (
    <header className="ca-theme-transition sticky top-0 z-50">
      <div className="relative left-1/2 w-screen -translate-x-1/2 border-b border-(--color-border-default) bg-[color-mix(in_srgb,var(--color-bg-surface-raised)_92%,var(--color-bg-page-secondary))] shadow-(--shadow-md)">
        <div className="mx-auto flex w-full max-w-[1740px] flex-col gap-3 px-4 py-4 sm:px-6 lg:px-10">
          <div className="flex flex-wrap items-center gap-3 xl:flex-nowrap">
            <a href={homeHref} className="flex min-w-0 items-center gap-3 xl:shrink-0">
              {homeSettings.logoUrl ? (
                <img
                  src={homeSettings.logoUrl}
                  alt={homeSettings.shopName || 'Logo'}
                  className="h-12 w-12 rounded-xl border border-(--color-border-default) bg-(--color-bg-card) object-contain p-1 shadow-(--shadow-sm)"
                />
              ) : (
                <div
                  className="grid h-12 w-12 place-items-center rounded-xl text-lg font-semibold text-slate-900 shadow-lg"
                  style={{ background: homeSettings.primaryColor || 'var(--theme-color-primary, #047857)' }}
                >
                  {(homeSettings.shopName || 'EC').trim().slice(0, 2).toUpperCase()}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-(--color-text-primary) sm:text-3xl">
                  <span className="ca-theme-transition hover:text-(--color-text-secondary)">
                    {homeSettings.shopName || 'Ecommerce'}
                  </span>
                </h1>
                {homeSettings.headerTagline && (
                  <p className="truncate text-sm text-(--color-text-secondary)">{homeSettings.headerTagline}</p>
                )}
              </div>
            </a>

            <ShopSearchBar
              placeholder={language === 'en' ? 'Search products' : 'Cerca prodotti'}
              ctaLabel={language === 'en' ? 'Search' : 'Cerca'}
              toLocalizedPath={toLocalizedPath}
              className="order-3 w-full xl:order-none xl:mx-auto xl:max-w-[900px] xl:flex-1"
            />

            <div className="ml-auto flex flex-wrap items-center justify-end gap-2 xl:shrink-0">
              <button
                type="button"
                className="ca-theme-transition relative inline-flex h-11 w-11 items-center justify-center rounded-lg border border-(--color-border-default) bg-(--color-bg-card) text-(--color-text-secondary) shadow-(--shadow-sm) hover:border-(--color-border-strong) hover:text-(--color-text-primary)"
                title={t('home.fallback.cart')}
                aria-label={t('home.fallback.cart')}
                onClick={() => {
                  clearError()
                  openDrawer()
                }}
              >
                <CartIcon className="h-4.5 w-4.5" />
                {itemCount > 0 && (
                  <span
                    className="absolute -right-1 -top-1 min-w-[18px] rounded-md px-1 text-center text-[10px] font-bold text-white"
                    style={{ background: 'var(--theme-color-primary, #10b981)' }}
                  >
                    {itemCount}
                  </span>
                )}
              </button>
              {authenticated ? (
                <HeaderCustomerMenu
                  authenticated={authenticated}
                  authBusy={authBusy}
                  profileName={profileName}
                  profileInitials={profileInitials}
                  logout={logout}
                  toLocalizedPath={toLocalizedPath}
                  labels={customerMenuLabels}
                />
              ) : (
                <a
                  href={backofficeHref}
                  className="ca-theme-transition relative inline-flex h-11 w-11 items-center justify-center rounded-lg border border-(--color-border-default) bg-(--color-bg-card) text-(--color-text-secondary) shadow-(--shadow-sm) hover:border-(--color-border-strong) hover:text-(--color-text-primary)"
                  title={t('home.fallback.adminArea')}
                  aria-label={t('home.fallback.adminArea')}
                >
                  <BackofficeIcon className="h-5 w-5" />
                  {isAdmin && (
                    <span
                      className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border border-(--color-bg-card) bg-emerald-500 shadow-sm"
                      title="Connesso"
                      aria-label="Connesso"
                    />
                  )}
                </a>
              )}
            </div>
          </div>

          <nav className="ca-theme-transition flex items-center gap-2 overflow-x-auto whitespace-nowrap pt-2.5 text-sm font-semibold text-(--color-text-secondary) [scrollbar-width:none] md:flex-wrap md:justify-center md:overflow-visible md:whitespace-normal [&::-webkit-scrollbar]:hidden">
            {links.map((item, itemIndex) => {
              const children = Array.isArray(item.children) ? item.children : []
              const hasChildren = children.length > 0

              if (!hasChildren) {
                return (
                  <a
                    key={`${item.label}-${item.href}-${itemIndex}`}
                    href={item.href}
                    className="ca-theme-transition inline-flex min-h-10 shrink-0 items-center rounded-lg border border-transparent px-3 py-1.5 hover:border-(--color-border-default) hover:bg-(--color-bg-surface-muted) hover:text-(--color-text-primary)"
                  >
                    {resolveLinkLabel(item, locale)}
                  </a>
                )
              }

              return (
                <div key={`${item.label}-${item.href}-${itemIndex}`} className="group relative shrink-0 md:shrink">
                  <a
                    href={item.href}
                    className="ca-theme-transition inline-flex min-h-10 items-center gap-1 rounded-lg border border-transparent px-3 py-1.5 hover:border-(--color-border-default) hover:bg-(--color-bg-surface-muted) hover:text-(--color-text-primary)"
                    aria-haspopup="menu"
                  >
                    <span>{resolveLinkLabel(item, locale)}</span>
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current stroke-[2]">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </a>

                  <div className="ca-theme-transition invisible absolute left-0 top-full z-30 mt-1 min-w-[220px] -translate-y-1 rounded-xl border border-(--color-border-default) bg-(--color-bg-card) p-1.5 opacity-0 shadow-(--shadow-md) transition duration-150 ease-out group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100 md:group-hover:visible md:group-focus-within:visible">
                    {children.map((child, childIndex) => (
                      <a
                        key={`${item.label}-${resolveLinkLabel(child, locale)}-${child.href}-${childIndex}`}
                        href={child.href}
                        className="ca-theme-transition block rounded-lg px-3 py-2 text-sm font-semibold text-(--color-text-secondary) hover:bg-(--color-bg-surface-muted) hover:text-(--color-text-primary)"
                      >
                        {resolveLinkLabel(child, locale)}
                      </a>
                    ))}
                  </div>
                </div>
              )
            })}
          </nav>
        </div>
      </div>
    </header>
  )
}
